import { useSyncExternalStore } from 'react';

/**
 * Breakpoints canônicos (BR-WEB-01). Fonte única de verdade em JS — os mesmos valores dos tokens
 * `--bp-tablet`/`--bp-desktop` em `zr-tokens.css`. phone < 768 · tablet 768–1023 · desktop ≥ 1024.
 */
export const BREAKPOINTS = {
  tablet: 768,
  desktop: 1024,
} as const;

export type Breakpoint = 'phone' | 'tablet' | 'desktop';

/** Mapeia uma largura em px para o breakpoint canônico (BR-WEB-01). */
export function resolveBreakpoint(width: number): Breakpoint {
  if (width >= BREAKPOINTS.desktop) return 'desktop';
  if (width >= BREAKPOINTS.tablet) return 'tablet';
  return 'phone';
}

const DESKTOP_QUERY = `(min-width: ${String(BREAKPOINTS.desktop)}px)`;
const TABLET_QUERY = `(min-width: ${String(BREAKPOINTS.tablet)}px)`;

function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => undefined;
  }
  const lists = [window.matchMedia(TABLET_QUERY), window.matchMedia(DESKTOP_QUERY)];
  for (const list of lists) list.addEventListener('change', onChange);
  return () => {
    for (const list of lists) list.removeEventListener('change', onChange);
  };
}

function getSnapshot(): Breakpoint {
  return resolveBreakpoint(window.innerWidth);
}

/** SSR/preview sem `window`: assume desktop (base do layout desktop-first). */
function getServerSnapshot(): Breakpoint {
  return 'desktop';
}

/**
 * Retorna o breakpoint ativo (BR-WEB-02). Use para troca estrutural no React — ex.: renderizar
 * cards em `phone` e tabela em `desktop` — quando CSS sozinho não resolve. Para ajuste puramente
 * visual, prefira CSS/`@media` com os cortes canônicos.
 */
export function useBreakpoint(): Breakpoint {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Açúcar: `true` quando o breakpoint ativo é `phone`. */
export function useIsPhone(): boolean {
  return useBreakpoint() === 'phone';
}
